import { z } from 'zod';

/**
 * O que a App Store Connect manda quando o estado de uma versão muda, e o que
 * daí se aproveita.
 *
 * Contexto (#655): o `latestBuild` do iOS precisa subir quando a versão fica
 * **disponível na loja**, e não quando o binário sobe para o TestFlight — senão
 * o banner diz "atualize" para quem não tem o que baixar. O webhook oficial da
 * Apple é o único sinal desse instante; o `itunes.apple.com/lookup` não serve,
 * porque devolve a `versionString` do registro da loja ("1.0"), que não tem
 * relação com o build monotônico que o gate compara.
 */

/**
 * Estados conhecidos de uma versão na App Store Connect, medidos na doc em
 * 2026-09-16. A lista é documentação e ponto de comparação — **não** é um enum
 * fechado no wire.
 *
 * O estado chega como `string` de propósito. A Apple acrescenta valor sem
 * avisar, e a única pergunta que este código faz é "já está à venda?". Um enum
 * fechado trocaria "ignoro o que não conheço" por "o parser falha, devolvo 500
 * e a Apple re-entrega para sempre" — o pior desfecho possível para um estado
 * que sequer nos interessa.
 */
export const KNOWN_APP_VERSION_STATES = [
  'ACCEPTED',
  'DEVELOPER_REJECTED',
  'IN_REVIEW',
  'INVALID_BINARY',
  'METADATA_REJECTED',
  'PENDING_APPLE_RELEASE',
  'PENDING_DEVELOPER_RELEASE',
  'PREPARE_FOR_SUBMISSION',
  'PROCESSING_FOR_DISTRIBUTION',
  'READY_FOR_DISTRIBUTION',
  'READY_FOR_REVIEW',
  'REJECTED',
  'REPLACED_WITH_NEW_VERSION',
  'WAITING_FOR_EXPORT_COMPLIANCE',
  'WAITING_FOR_REVIEW',
] as const;

/** O estado em que a versão está de fato à venda. É o único que dispara escrita. */
export const APP_VERSION_STATE_PUBLISHED = 'READY_FOR_DISTRIBUTION';

/**
 * O corpo do evento `APP_STORE_VERSION_APP_VERSION_STATE_UPDATED`.
 *
 * O payload **não traz o build nem a versionString** — só o id do
 * `appStoreVersion`, que obriga uma consulta à API. Isso foi conferido na doc e
 * medido contra o app em 2026-09-16; quem assumir o contrário escreve um parser
 * que nunca acha o número.
 */
export const AppStoreVersionStateEventSchema = z.object({
  data: z.object({
    type: z.string(),
    /** Chave de idempotência: a Apple re-entrega (`webhookDeliveries.redelivery`). */
    id: z.string().min(1),
    attributes: z.object({
      newValue: z.string().min(1),
      oldValue: z.string().nullish(),
      timestamp: z.string().nullish(),
    }),
    relationships: z.object({
      instance: z.object({
        data: z.object({
          type: z.string(),
          id: z.string().min(1),
        }),
      }),
    }),
  }),
});
export type AppStoreVersionStateEvent = z.infer<typeof AppStoreVersionStateEventSchema>;

/** O tipo que identifica o evento que nos interessa, em `data.type`. */
export const APP_STORE_VERSION_STATE_EVENT_TYPE = 'appStoreVersionAppVersionStateUpdated';

/**
 * O ping de verificação (`POST /v1/webhookPings`), que serve para provar o
 * endpoint sem esperar um release. Ele **não** tem `relationships`, então cair
 * no parser do evento normal o faria parecer payload inválido.
 */
export const AppStoreWebhookPingSchema = z.object({
  data: z.object({
    type: z.string(),
    id: z.string().min(1),
    attributes: z.object({ ping: z.literal(true) }),
  }),
});
